"use client";

import Link from "next/link";
import { ArrowRight, Sparkles, TrendingUp, Brain, Globe, Layers, Zap } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";
import { executiveSummary as data } from "@/lib/data/capabilities-narrative-engineering";

const metricIcons = [TrendingUp, Brain, Globe, Layers, Zap] as const;
const accents = [
  "var(--color-crimson)",
  "var(--color-purple)",
  "var(--color-cyan)",
  "var(--color-emerald)",
  "var(--color-amber)",
] as const;

export function ExecutiveSummary() {
  return (
    <Section id="executive-summary" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/3 left-1/4 h-80 w-80 rounded-full bg-[var(--color-purple)] opacity-15 blur-[120px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-purple)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
          </div>
        </Reveal>

        <div className="mt-10 max-w-4xl space-y-5">
          <Reveal>
            <p className="text-lg font-medium text-[rgb(var(--fg))] leading-snug">
              {data.intro}
            </p>
          </Reveal>
          {data.paragraphs.map((p, i) => (
            <Reveal key={i} delay={i * 60 + 100}>
              <p className="text-body leading-relaxed text-[rgb(var(--fg-muted))]">{p}</p>
            </Reveal>
          ))}
        </div>

        <Reveal delay={300}>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 max-w-5xl">
            {data.metrics.map((m, i) => {
              const Icon = metricIcons[i % metricIcons.length]!;
              const accent = accents[i % accents.length]!;
              return (
                <div
                  key={m.label}
                  className="glass rounded-2xl p-5"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)`,
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="h-4 w-4" style={{ color: accent }} />
                    <Sparkles className="h-3 w-3" style={{ color: accent }} />
                  </div>
                  <div className="text-2xl font-bold" style={{ color: accent }}>
                    {m.value}
                  </div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-[rgb(var(--fg-subtle))] leading-tight mt-1">
                    {m.label}
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={400}>
          <div className="mt-10">
            <Link
              href={data.cta.href}
              className="btn-primary-gradient h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base"
            >
              {data.cta.label} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
